/**
 * UI utility functions for cursor management and selection
 */

/**
 * Show loading cursor
 */
export function showLoadingCursor() {
  document.body.classList.add("cursor-wait");
  document.body.classList.remove("cursor-default", "cursor-crosshair");
}

/**
 * Hide loading cursor (restore default)
 */
export function hideLoadingCursor() {
  document.body.classList.remove("cursor-wait", "cursor-crosshair", "cursor-default");
}

/**
 * Clear all furniture item selections in the UI
 */
export function clearFurnitureSelection() {
  document.querySelectorAll(".furniture-item").forEach((item) => {
    item.classList.remove("selected");
  });
}

/**
 * Parse the `white_room_custom_floor` value — old format was a bare dataURL, current is JSON.
 * @param {string} raw
 * @returns {{dataURL: string, realW: number|null, realH: number|null}}
 */
export function parseFloorStorage(raw) {
  if (raw.startsWith("data:")) return { dataURL: raw, realW: null, realH: null };
  try {
    const parsed = JSON.parse(raw);
    // Native stores the image as a file and keeps only its URL; older records (and web)
    // carry the data URL inline. Normalise so callers see one field.
    if (parsed?.src && !parsed.dataURL) return { ...parsed, dataURL: parsed.src };
    return parsed;
  } catch {
    return { dataURL: raw, realW: null, realH: null };
  }
}

// The canvas container, not the window: Layout 2's sheet insets it. Window is the pre-boot fallback.
export function viewportSize() {
  const el = document.getElementById('canvas-container');
  const w = el?.clientWidth || window.innerWidth;
  const h = el?.clientHeight || window.innerHeight;
  return { width: w, height: h };
}

/** Stack a canvas over the app's realtime canvas, ignoring input so the controls underneath still work. */
export function mountOverlayCanvas(app, canvas, { className, zIndex = "" } = {}) {
  if (className) canvas.classList.add(className);
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = zIndex;
  (app.getRenderer?.()?.domElement?.parentElement ?? document.body).appendChild(canvas);
}

/** Match `target`'s size and pixel ratio to `source`, writing only on change; `cache` is `{ size, scratch: Vector2, pixelRatio }`. */
export function syncRendererSize(source, target, cache) {
  const pixelRatio = source.getPixelRatio();
  const size = source.getSize(cache.scratch);
  if (pixelRatio === cache.pixelRatio && size.equals(cache.size)) return false;
  cache.pixelRatio = pixelRatio;
  cache.size.copy(size);
  target.setPixelRatio(pixelRatio);
  target.setSize(size.x, size.y, false);
  return true;
}

/** Toggle an element's display, touching the style only when it changes. */
export function setDisplayed(element, shown) {
  const display = shown ? "" : "none";
  if (element && element.style.display !== display) element.style.display = display;
}

/** Mark the tab whose `idOf` is `id` selected (roving tabindex) and return it. */
export function selectTab(tabs, idOf, id, focus = false) {
  let selected = null;
  for (const tab of tabs) {
    const on = idOf(tab) === id;
    tab.setAttribute("aria-selected", String(on));
    tab.tabIndex = on ? 0 : -1;
    if (on) selected = tab;
  }
  if (focus) selected?.focus();
  return selected;
}

/** Click and WAI-ARIA arrow/Home/End keys on a tablist, each calling `activate(id, { focus })`. */
export function bindTabList(list, getTabs, idOf, currentId, activate) {
  if (!list) return;
  list.addEventListener("click", (e) => {
    const tab = getTabs().find((t) => t.contains(e.target));
    if (tab) activate(idOf(tab));
  });
  list.addEventListener("keydown", (e) => {
    const tabs = getTabs();
    const i = tabs.findIndex((t) => idOf(t) === currentId());
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    activate(idOf(tabs[(next + tabs.length) % tabs.length]), { focus: true });
  });
}
